// frontend/src/components/Layout/PartnerLayout.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import WalletWidget from '../common/WalletWidget';
import useNotifications from '../../hooks/useNotifications';
import { useNotificationAlert } from '../../context/NotificationAlertContext';
import NotificationPanel from '../notifications/NotificationPanel';
import NotificationAlert from '../notifications/NotificationAlert';
import logo from '../../assets/logoproxy.png';
import {
  BellIcon,
  ChevronDownIcon,
  ChartPieIcon,
  ShoppingCartIcon,
  CubeIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  CreditCardIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/solid';
import './PartnerLayout.css';

const PartnerLayout = ({ children }) => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const profileRef = useRef(null);

  const [user, setUser]               = useState(null);
  const [wallet, setWallet]           = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showPanel, setShowPanel]     = useState(false);

  const { notifications, unreadCount, markRead, markAllRead, remove } = useNotifications();
  const { triggerAlert } = useNotificationAlert();

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    fetchWallet();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await API.get('/wallet/my-wallet');
      setWallet(res.data.wallet);
    } catch (_) {}
  };

  const handleNotifClick = (notif) => {
    triggerAlert(notif.title);
    if (notif.target_url) navigate(notif.target_url);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    navigate('/login/partner');
  };

  const getInitials = () => {
    if (!user) return 'P';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  /* ── Navigation ── */
  const navLinks = [
    { path: '/dashboard/partner',               label: 'Dashboard'   },
    { path: '/dashboard/partner/services',      label: 'Services'    },
    { path: '/dashboard/partner/wallet',        label: 'Portefeuille'},
    { path: '/dashboard/partner/sales',         label: 'Ventes'      },
    { path: '/dashboard/partner/billing',       label: 'Facturation' },
    { path: '/dashboard/partner/api',           label: 'API'         },
  ];

  const iconStyle = { width: 18, height: 18, flexShrink: 0 };
  const sideItems = [
    { path: '/dashboard/partner',               icon: <ChartPieIcon style={iconStyle} />,    label: 'Dashboard'    },
    { path: '/dashboard/partner/purchase-packs',icon: <ShoppingCartIcon style={iconStyle} />, label: 'Achats B2B'   },
    { path: '/dashboard/partner/services',      icon: <CubeIcon style={iconStyle} />,         label: 'Services'     },
    { path: '/dashboard/partner/b2c-packs',     icon: <ArchiveBoxIcon style={iconStyle} />,   label: 'Packs B2C'    },
    { path: '/dashboard/partner/wallet',        icon: <BanknotesIcon style={iconStyle} />,    label: 'Portefeuille' },
    { path: '/dashboard/partner/sales',         icon: <CreditCardIcon style={iconStyle} />,   label: 'Ventes'       },
    { path: '/dashboard/partner/billing',       icon: <DocumentTextIcon style={iconStyle} />, label: 'Facturation'  },
    { path: '/dashboard/partner/api',           icon: <CodeBracketIcon style={iconStyle} />,  label: 'API'          },
    { path: '/dashboard/partner/settings',      icon: <Cog6ToothIcon style={iconStyle} />,    label: 'Paramètres'   },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="pl-layout">

      {/* ═══════════════════════════════════════
          TOP NAVBAR  —  glass · centré · 3D
      ═══════════════════════════════════════ */}
      <header className="pl-navbar">

        {/* Brand */}
        <div className="pl-brand" onClick={() => navigate('/dashboard/partner')}>
          <img src={logo} alt="POINTPAY" className="logo-img" />
        </div>

        {/* Liens horizontaux centrés */}
        <nav className="pl-nav-links">
          {navLinks.map(link => (
            <button
              key={link.path}
              className={`pl-nav-link ${isActive(link.path) ? 'active' : ''}`}
              onClick={() => navigate(link.path)}
            >
              {link.label}
              {isActive(link.path) && (
                <motion.div
                  className="pl-nav-link-bar"
                  layoutId="partnerNavBar"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>

        {/* Actions droite */}
        <div className="pl-nav-right">

          {/* Wallet widget */}
          <WalletWidget points={wallet?.available_points || 0} />

          {/* Cloche */}
          <motion.button
            className={`pl-nav-icon-btn${showPanel ? ' active' : ''}`}
            onClick={() => { setShowPanel(true); setShowProfile(false); }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            title="Notifications"
          >
            <BellIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            {unreadCount > 0 && (
              <motion.span
                className="pl-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </motion.span>
            )}
          </motion.button>

          {/* Avatar + dropdown */}
          <div className="pl-avatar-wrap" ref={profileRef}>
            <motion.button
              className={`pl-avatar-btn${showProfile ? ' active' : ''}`}
              onClick={() => setShowProfile(v => !v)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="pl-avatar">{getInitials()}</div>
              <div className="pl-avatar-info">
                <span className="pl-avatar-name">{user?.company_name || user?.first_name}</span>
                <span className="pl-avatar-role">Partenaire</span>
              </div>
              <motion.div
                className="pl-chevron"
                animate={{ rotate: showProfile ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {showProfile && (
                <motion.div
                  className="pl-dropdown"
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 26 } }}
                  exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.14 } }}
                >
                  <div className="pl-dd-header">
                    <div className="pl-dd-avatar">{getInitials()}</div>
                    <div>
                      <div className="pl-dd-name">{user?.first_name} {user?.last_name}</div>
                      <div className="pl-dd-company">{user?.company_name}</div>
                      <div className="pl-dd-email">{user?.email}</div>
                    </div>
                  </div>
                  <div className="pl-dd-sep" />
                  <button className="pl-dd-item" onClick={() => { navigate('/dashboard/partner'); setShowProfile(false); }}>
                    <ChartPieIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Dashboard
                  </button>
                  <button className="pl-dd-item" onClick={() => { navigate('/dashboard/partner/settings'); setShowProfile(false); }}>
                    <Cog6ToothIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Paramètres
                  </button>
                  <button className="pl-dd-item" onClick={() => { navigate('/dashboard/partner/api'); setShowProfile(false); }}>
                    <CodeBracketIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> API & Clés
                  </button>
                  <div className="pl-dd-sep" />
                  <button className="pl-dd-item pl-dd-danger" onClick={handleLogout}>
                    <ArrowRightOnRectangleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Déconnexion
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════
          BODY  =  Mini sidebar  +  Content
      ═══════════════════════════════════════ */}
      <div className="pl-body">

        {/* Mini sidebar icônes */}
        <aside className="pl-sidebar">
          {sideItems.map(item => (
            <div key={item.path} className="pl-side-item-wrap">
              <motion.button
                className={`pl-side-item${isActive(item.path) ? ' active' : ''}`}
                onClick={() => navigate(item.path)}
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.92 }}
              >
                {item.icon}
              </motion.button>
              <div className="pl-side-tooltip">{item.label}</div>
            </div>
          ))}
        </aside>

        {/* Main */}
        <main className="pl-main">
          <NotificationAlert />
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              className="pl-page"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Notifications panel */}
      <NotificationPanel
        open={showPanel}
        onClose={() => setShowPanel(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onRemove={remove}
        onNotifClick={handleNotifClick}
      />
    </div>
  );
};

export default PartnerLayout;
